/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CertificateTemplateParts
 * ------------------------
 * What the two Certificate Templates pages share: the API helper, the
 * application and its organization, the template list and which templates an
 * event activity uses, the form's field and notice blocks, and the preview.
 *   CertificateTemplatesPage      the list (Preview, Edit, Delete, Add)
 *   CertificateTemplateFormPage   add / edit on its own page
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { readLookup } from './lookupOptions';
import { CERTIFICATE_TEMPLATE_TABLE, CHOICE_TABLE, sortTemplates, usageByTemplate } from './certificateTemplates';

export const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** The application and its organization id. */
export function useApplication(applicationId) {
  const query = useQuery({
    queryKey: ['application', applicationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(`/arcee/application/details/${applicationId}/`, { method: 'GET', signal });
      if (!resp.ok) throw new Error('Could not load this application.');
      return resp.json();
    },
    enabled: !!applicationId,
    staleTime: 60_000,
  });
  const application = query.data;
  const organizationId = application?.organization?.id
    ?? application?.organization_id
    ?? (typeof application?.organization === 'number' ? application.organization : undefined);
  return { application, organizationId };
}

export const templatesKey = (applicationId, organizationId) => ['certificateTemplates', applicationId, organizationId];

async function fetchRows(table, organizationId, applicationId, signal) {
  const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      table_name: table, organization_id: organizationId, application_id: Number(applicationId), page_size: 1000,
    }),
  });
  if (!resp.ok) throw new Error(`Could not load ${table}.`);
  return readLookup(await resp.json()).rows;
}

/** Every certificate template, active first, by name. */
export function useTemplates(applicationId, organizationId) {
  return useQuery({
    queryKey: templatesKey(applicationId, organizationId),
    queryFn: async ({ signal }) => sortTemplates(await fetchRows(CERTIFICATE_TEMPLATE_TABLE, organizationId, applicationId, signal)),
    enabled: !!organizationId && !!applicationId,
    staleTime: 0,
  });
}

/** { templateId: number of event activities using it } — a used template cannot be deleted. */
export function useTemplateUsage(applicationId, organizationId) {
  return useQuery({
    queryKey: ['certificateTemplateUsage', applicationId, organizationId],
    queryFn: async ({ signal }) => usageByTemplate(await fetchRows(CHOICE_TABLE, organizationId, applicationId, signal)),
    enabled: !!organizationId && !!applicationId,
    staleTime: 0,
  });
}

export const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 };
export const hint = { fontSize: 12, color: '#6b7280', marginTop: 4 };
export const errorText = { fontSize: 12, color: '#d53b3b', marginTop: 4 };

export function Field({ title, help, error, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={label}>{title}</label>
      {children}
      {help && <div style={hint}>{help}</div>}
      {error && <div style={errorText}>{error}</div>}
    </div>
  );
}

export function Notice({ children, tone = 'info' }) {
  const tones = {
    info: { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e3a8a' },
    warn: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e' },
  };
  const t = tones[tone];
  return (
    <div style={{
      background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
      borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 14,
    }}>
      {children}
    </div>
  );
}

export function PreviewModal({ template, onClose }) {
  const [state, setState] = useState({ loading: true, html: '', error: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(template.template_url, { cache: 'no-cache' });
        if (!resp.ok) throw new Error(`The template file could not be read (${resp.status}).`);
        const html = await resp.text();
        if (!cancelled) setState({ loading: false, html, error: '' });
      } catch (err) {
        if (!cancelled) setState({ loading: false, html: '', error: err.message || 'The template file could not be read.' });
      }
    })();
    return () => { cancelled = true; };
  }, [template]);

  // A4 landscape is 297 × 210 mm ≈ 1123 × 794 CSS px; shown at 70%.
  const scale = 0.7;
  return (
    <div
      role="dialog" aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 16, maxWidth: '96vw', maxHeight: '94vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong>{template.name || 'Template preview'}</strong>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
          Placeholders such as {'{{ name }}'} and {'{{ organization_name }}'} are filled when a participant downloads.
        </div>
        {state.loading && <div style={{ padding: 24 }}>Loading…</div>}
        {state.error && <div style={{ padding: 24, color: '#d53b3b' }}>{state.error}</div>}
        {!state.loading && !state.error && (
          <div style={{ width: 1123 * scale, height: 794 * scale, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {/* sandbox with no permissions: the uploaded HTML cannot run scripts. */}
            <iframe
              title={`${template.name || 'Template'} preview`}
              sandbox=""
              srcDoc={state.html}
              style={{ width: 1123, height: 794, border: 0, transform: `scale(${scale})`, transformOrigin: '0 0' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

